// Libs
import classNames from 'classnames/bind';
import { useTranslation } from 'react-i18next';
import { Box, Stack } from '@mui/material';
import { useCallback, useContext, useEffect, useState } from 'react';
import { useLocation, useSearchParams } from 'react-router-dom';
// Components, Layouts, Pages
import { BaseToolbar } from '~/components/common';
import PageShell from '~/pages/common/pageShell/PageShell';
import { Conversation } from '~/components/module/conversation';
import { ListConversation } from '~/components/module/conversation';
// Others
import { WebSocketContext } from '~/context';
import { joinListConversation, leaveListConversation } from '~/utils/helper/websocket';
import { IConversation } from '~/utils/interface/conversation';
import useDebounce from '~/utils/hooks/useDebounce';
import {
  BASE_PAGINATION_DEFAULT_CURRENT_PAGE,
  DEFAULT_DEBOUNCE_DELAY_TIME,
  DEFAULT_LIMIT_PAGE,
  DEFAULT_NUMBER_ONE,
  DEFAULT_NUMBER_ZERO,
  EMPTY_STRING,
} from '~/utils/constants/common';
import { IQueryParamBase } from '~/utils/interface/common';
import { useAppDispatch } from '~/utils/redux/hooks';
import { getListConversation } from '~/thunks/conversation/conversationThunk';
import { WebSocketEventEnum } from '~/utils/enum/websocket';
// Styles, images, icons
import styles from './Message.module.scss';

type Props = {};

const cx = classNames.bind(styles);

const Message = (props: Props) => {
  //#region Destructuring Props

  //#endregion Destructuring Props

  //#region Declare Hook
  const { t } = useTranslation(['admin/message']);
  const dispatch = useAppDispatch();
  const location = useLocation();
  const queryParams = new URLSearchParams(location.search);
  const conversationId = queryParams.get('conversationId');
  const { message: messageWSS } = useContext(WebSocketContext);
  const { wss } = useContext(WebSocketContext);
  const [, setSearchParams] = useSearchParams();
  //#endregion Declare Hook

  //#region Selector
  //#endregion Selector

  //#region Declare State
  const [conversationList, setConversationList] = useState<IConversation[]>([]);
  const [currentConversationPagination, setCurrentConversationPagination] = useState<number>(
    BASE_PAGINATION_DEFAULT_CURRENT_PAGE
  );
  const [searchKey, setSearchKey] = useState<string>(EMPTY_STRING);
  const debounceSearchKey = useDebounce(searchKey, DEFAULT_DEBOUNCE_DELAY_TIME);
  const [conversationSelected, setConversationSelected] = useState<string>();
  const [isLoadMore, setIsLoadingMore] = useState<boolean>(false);
  const [hasMoreConversation, setHasMoreConversation] = useState<boolean>(true);
  //#endregion Declare State

  //#region Declare Memo
  //#endregion Declare Memo

  const handleGetListConversation = useCallback(
    (params: IQueryParamBase) => {
      dispatch(getListConversation(params))
        .unwrap()
        .then((res) => {
          const { responses, pagination } = res?.data;

          if (!responses || responses?.length === DEFAULT_NUMBER_ZERO) {
            setHasMoreConversation(false);

            pagination?.page === DEFAULT_NUMBER_ONE && setConversationList([]);

            return;
          }

          setCurrentConversationPagination(pagination?.page);
          if (isLoadMore) {
            setConversationList((prevState) => [...prevState, ...responses]);
            return;
          }

          setConversationList((prev) =>
            responses.map((conversation) => {
              const existConversation = prev.find((item) => item.id === conversation.id);
              return { ...conversation, isTyping: existConversation?.isTyping };
            })
          );
        })
        .catch((error) => {
          setHasMoreConversation(false);
        })
        .finally(() => {
          setIsLoadingMore(false);
        });
    },
    [dispatch, isLoadMore]
  );

  const handleRefreshConversation = useCallback(() => {
    setCurrentConversationPagination(BASE_PAGINATION_DEFAULT_CURRENT_PAGE);
    const params: IQueryParamBase = {
      page: BASE_PAGINATION_DEFAULT_CURRENT_PAGE,
      limit: DEFAULT_LIMIT_PAGE,
      ...(debounceSearchKey && { textSearch: debounceSearchKey }),
    };
    setHasMoreConversation(true);

    handleGetListConversation(params);
  }, [debounceSearchKey, handleGetListConversation]);

  //#region Implement Hook
  useEffect(() => {
    if (wss) {
      joinListConversation(wss);
    }

    return () => {
      if (wss) {
        leaveListConversation(wss);
      }
    };
  }, [wss]);

  useEffect(() => {
    if (!conversationId) return;

    setConversationSelected(conversationId);
  }, [conversationId]);

  useEffect(() => {
    const params: IQueryParamBase = {
      page: currentConversationPagination,
      limit: DEFAULT_LIMIT_PAGE,
      ...(debounceSearchKey && { textSearch: debounceSearchKey }),
    };

    handleGetListConversation(params);
  }, [currentConversationPagination, debounceSearchKey, handleGetListConversation]);

  useEffect(() => {
    const params: IQueryParamBase = {
      page: BASE_PAGINATION_DEFAULT_CURRENT_PAGE,
      limit: DEFAULT_LIMIT_PAGE,
      ...(debounceSearchKey && { textSearch: debounceSearchKey }),
    };

    handleGetListConversation(params);

    if (!debounceSearchKey) {
      setHasMoreConversation(true);
    }
  }, [debounceSearchKey, handleGetListConversation]);

  useEffect(() => {
    if (!messageWSS) {
      return;
    }

    switch (messageWSS.type) {
      case WebSocketEventEnum.SEND_MESSAGE:
        handleRefreshConversation();
        break;
      case WebSocketEventEnum.TYPING:
        setConversationList((prev) =>
          prev.map((conversation) =>
            conversation.id === messageWSS?.data?.conversationId?.toString()
              ? { ...conversation, isTyping: true }
              : conversation
          )
        );
        break;
      case WebSocketEventEnum.STOP_TYPING:
        setConversationList((prev) =>
          prev.map((conversation) =>
            conversation.id === messageWSS?.data?.conversationId?.toString()
              ? { ...conversation, isTyping: false }
              : conversation
          )
        );
        break;
      case WebSocketEventEnum.SEEN_MESSAGE:
        handleRefreshConversation();
        break;
      case WebSocketEventEnum.DELIVERED_MESSAGE:
        handleRefreshConversation();
        break;
      case WebSocketEventEnum.ONLINE:
        handleRefreshConversation();
        break;
      case WebSocketEventEnum.OFFLINE:
        handleRefreshConversation();
        break;
      default:
        break;
    }
  }, [messageWSS, handleRefreshConversation]);
  //#endregion Implement Hook

  //#region Handle Function
  const handleSearchChange = (value: string) => {
    setSearchKey(value);
  };

  const handleConversationClick = (id: string) => {
    setConversationSelected(id);
    setSearchParams({ conversationId: id });
  };

  const handleLoadMoreConversation = () => {
    setIsLoadingMore(true);
    setCurrentConversationPagination((prev) => prev + DEFAULT_NUMBER_ONE);
  };

  //#endregion Handle Function

  return (
    <PageShell title={t('message_title')}>
    <Box component={'div'} id='messagePage' className={cx('container')}>
      <BaseToolbar />

      <Stack component={'div'} direction={'row'} gap={'24px'} className={cx('body')}>
        <Box component={'div'} className={cx('listConversation')}>
          <ListConversation
            conversations={conversationList || []}
            conversationSelected={conversationSelected}
            onSearch={handleSearchChange}
            searchValue={searchKey}
            onConversationClick={handleConversationClick}
            hasMoreConversation={hasMoreConversation}
            onLoadMoreConversation={handleLoadMoreConversation}
          />
        </Box>

        <Box component={'div'} className={cx('messageConversation')}>
          <Conversation
            key={conversationSelected}
            conversationId={conversationSelected}
            onRefreshConversation={handleRefreshConversation}
          />
        </Box>
      </Stack>
    </Box>
    </PageShell>
  );
};

export default Message;
